import dayjs from 'dayjs';
import isTodayPluggin from 'dayjs/plugin/isToday';
import minMaxPluggin from 'dayjs/plugin/minMax';

import { DateRange } from './types';

dayjs.extend(isTodayPluggin);
dayjs.extend(minMaxPluggin);

export const toDate = (date: string | Date) => dayjs(date).toDate();

export const isValid = (date: Date) => dayjs(date).isValid();

export const parseISO = (date: string) => new Date(dayjs(date).toISOString());

export const isToday = (date: Date) => dayjs(date).isToday();

export const isSameDay = (firstDate: Date, secondDate: Date) =>
  dayjs(firstDate).isSame(secondDate, 'day');

export const isSameMonth = (firstDate: Date, secondDate: Date) =>
  dayjs(firstDate).isSame(secondDate, 'month');

export const isAfter = (startDate: Date, endDate: Date) =>
  dayjs(startDate).isAfter(endDate);

export const isBefore = (startDate: Date, endDate: Date) =>
  dayjs(startDate).isBefore(endDate);

export const addYears = (date: Date, years: number) =>
  dayjs(date).add(years, 'years').toDate();

export const addMonths = (date: Date, months: number) =>
  dayjs(date).add(months, 'months').toDate();

export const addWeeks = (date: Date, weeks: number) =>
  dayjs(date).add(weeks, 'weeks').toDate();

export const addDays = (date: Date, days: number) =>
  dayjs(date).add(days, 'days').toDate();

export const startOfWeek = (date: Date) => dayjs(date).startOf('week').toDate();

export const startOfMonth = (date: Date) =>
  dayjs(date).startOf('month').toDate();

export const endOfWeek = (date: Date) => dayjs(date).endOf('week').toDate();

export const endOfMonth = (date: Date) => dayjs(date).endOf('month').toDate();

export const getDate = (date: Date) => dayjs(date).get('date');

export const getMonth = (date: Date) => dayjs(date).get('month');

export const getYear = (date: Date) => dayjs(date).get('year');

export const setMonth = (date: Date, value: number) =>
  dayjs(date).set('month', value).toDate();

export const setYear = (date: Date, value: number) =>
  dayjs(date).set('year', value).toDate();

export const format = (date: Date, template?: string) =>
  dayjs(date).format(template);

export const differenceInCalendarMonths = (startDate: Date, endDate: Date) =>
  dayjs(startDate).diff(endDate, 'months');

export const isWithinInterval = (
  day: Date,
  range: {
    start: Date;
    end: Date;
  }
) => dayjs(day).isAfter(range?.start) && dayjs(day).isBefore(range?.end);

export const min = (dates: Date[]) =>
  dayjs.min(dates.map((d) => dayjs(d))).toDate();

export const max = (dates: Date[]) =>
  dayjs.max(dates.map((d) => dayjs(d))).toDate();

export const identity = <T>(x: T) => x;

export const chunks = <T>(array: ReadonlyArray<T>, size: number): T[][] =>
  Array.from({ length: Math.ceil(array.length / size) }, (v, i) =>
    array.slice(i * size, i * size + size)
  );

export const combine = (...args: any[]): string =>
  args.filter(identity).join(' ');

// Date
export const getDaysInMonth = (date: Date) => {
  const startWeek = startOfWeek(startOfMonth(date));
  const endWeek = endOfWeek(endOfMonth(date));
  const days = [];
  for (let curr = startWeek; isBefore(curr, endWeek); ) {
    days.push(curr);
    curr = addDays(curr, 1);
  }
  return days;
};

export const isStartOfRange = ({ startDate }: DateRange, day: Date) =>
  startDate && isSameDay(day, startDate);

export const isEndOfRange = ({ endDate }: DateRange, day: Date) =>
  endDate && isSameDay(day, endDate);

export const inDateRange = ({ startDate, endDate }: DateRange, day: Date) =>
  startDate &&
  endDate &&
  (isWithinInterval(day, {
    start: startDate,
    end: endDate
  }) ||
    isSameDay(day, startDate) ||
    isSameDay(day, endDate));

export const isRangeSameDay = ({ startDate, endDate }: DateRange) => {
  if (startDate && endDate) {
    return isSameDay(startDate, endDate);
  }
  return false;
};

type Falsy = false | null | undefined | 0 | '';

export const parseOptionalDate = (
  date: Date | string | Falsy,
  defaultValue: Date
) => {
  if (date instanceof Date) {
    const parsed = toDate(date);
    if (isValid(parsed)) return parsed;
  }

  if (date instanceof String) {
    const parsed = parseISO(date as string);
    if (isValid(parsed)) return parsed;
  }

  return defaultValue;
};
